import clsx from 'clsx';
import _range from 'lodash/range';
import { useId, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { CloseIcon } from '@/components/icons/close';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Typography } from '@/components/ui/typography';

import { Skeleton } from './skeleton';

type Option = {
  label: string;
  value: string | boolean | number | undefined;
};

// ---------------------------------------------------------------------------
// AutocompleteCombobox — inline-search combobox where the trigger IS an input.
//
// Used for VONQ posting requirement fields:
//   - Pattern 2 (user-typed search): parent debounces `query` and fires the API
//   - Pattern 1 (lazy-loaded list): parent pre-fetches options, passes them in
//
// The parent owns `query` / `setQuery` so it can debounce and fetch. On
// selection, `setQuery` is called with the option label (so the input reflects
// the chosen value), and `onChange` is called with the option key (RHF value).
// ---------------------------------------------------------------------------

type AutocompleteComboboxProps = {
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
  query: string;
  setQuery: (q: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  isClearable?: boolean;
  error?: string;
  disabled?: boolean;
  id?: string;
};

export function AutocompleteCombobox({
  options,
  value,
  onChange,
  query,
  setQuery,
  placeholder,
  isLoading,
  isClearable,
  error,
  disabled,
  id,
}: AutocompleteComboboxProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const showClear = isClearable && value !== null && value !== '';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const handleSelect = (option: Option) => {
    onChange(String(option.value ?? ''));
    setQuery(String(option.label));
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  };

  const popoverWidth =
    open && containerRef.current ? containerRef.current.offsetWidth : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          ref={containerRef}
          role="combobox"
          aria-expanded={open}
          className={clsx(
            'border-fx-neutral-300 text-fx-neutral-900 flex h-9 w-full cursor-text items-center rounded-sm border bg-white px-2',
            open && 'border-fx-brand-600 shadow-selected',
            error && '!border-fx-rose',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <input
            ref={inputRef}
            id={id}
            value={query}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className="placeholder:text-fx-neutral-600 min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
          {showClear && (
            <span
              role="button"
              tabIndex={0}
              onMouseDown={handleClear}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear(e);
                }
              }}
              className="ml-1 cursor-pointer"
            >
              <CloseIcon className="text-fx-neutral-400 hover:text-fx-primary mt-0.5 h-4 w-4" />
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto !p-0"
        align="start"
        style={popoverWidth ? { width: `${popoverWidth}px` } : undefined}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {query.length === 0 && !isLoading ? (
              <CommandEmpty>
                <Typography variant="body1">
                  <FormattedMessage
                    defaultMessage="Start typing to search..."
                    description="Autocomplete combobox: prompt shown before the user types a search query"
                  />
                </Typography>
              </CommandEmpty>
            ) : (
              <ComboboxContent isLoading={isLoading} options={options}>
                <CommandGroup>
                  {options.map(option => (
                    <CommandItem
                      key={String(option.value)}
                      value={String(option.value)}
                      onSelect={() => handleSelect(option)}
                    >
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ComboboxContent>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type FormAutocompleteComboboxProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  required?: boolean;
  error?: string;
} & Omit<AutocompleteComboboxProps, 'value' | 'onChange'>;

export function FormAutocompleteCombobox<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  required,
  error,
  ...props
}: FormAutocompleteComboboxProps<TFieldValues>) {
  const id = useId();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="flex w-full flex-col gap-1">
          {label && (
            <label htmlFor={id}>
              <Typography variant="subtitle2">
                {label} {required && <span className="text-fx-rose">*</span>}
              </Typography>
            </label>
          )}
          <AutocompleteCombobox
            id={id}
            value={field.value as string | null}
            onChange={field.onChange}
            error={error}
            {...props}
          />
          {error && (
            <Typography variant="small1" className="!text-fx-rose">
              {error}
            </Typography>
          )}
        </div>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface ComboboxContentProps {
  isLoading?: boolean;
  options: Option[];
  children: React.ReactNode;
}

function ComboboxContent({
  isLoading,
  options,
  children,
}: ComboboxContentProps) {
  if (isLoading)
    return (
      <div className="space-y-4 p-4">
        {_range(5).map(i => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );

  if (!isLoading && options.length === 0)
    return (
      <CommandEmpty>
        <Typography variant="body1">
          <FormattedMessage
            defaultMessage="No options found!"
            description="Autocomplete combobox: search or API returned no selectable rows"
          />
        </Typography>
      </CommandEmpty>
    );

  return <>{children}</>;
}

// Re-export the Option type for consumers that build option arrays externally.
export type { Option };
